import { useState, useMemo, useEffect } from "react";
import allArtistComments from "../data/allArtistComments.json";
import "./comment.css";

/*
사용 예시 (부모 컴포넌트에서 버튼과 열림 상태를 관리):

import { useState } from "react";
import CommentSearch from "./comment_search";

function ParentScreen() {
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  return (
    <>
      <button onClick={() => setIsSearchOpen(true)}>댓글 검색</button>
      <CommentSearch isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  );
}
*/

// ---- 색상 토큰 ----
// 배경: 잉크 네이비, 포인트: 라디오 다이얼 앰버 + 웨이브폼 틸
const COLORS = {
  bg: "#12141A",
  surface: "#1B1E27",
  surfaceHover: "#232734",
  border: "#2C3040",
  amber: "#F2C744",
  teal: "#5EEAD4",
  text: "#F3F1EA",
  muted: "#8A8D9C",
};

function parseArtistComment(raw) {
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    return {
      artistName: parsed.artistName || "알 수 없는 아티스트",
      comment: parsed.comment || "",
      imgUrl: parsed.imgUrl || "",
    };
  } catch {
    return { artistName: "알 수 없는 아티스트", comment: "", imgUrl: "" };
  }
}

function formatDateKey(key) {
  // "20240705" -> "2024.07.05"
  if (/^\d{8}$/.test(key)) {
    return `${key.slice(0, 4)}.${key.slice(4, 6)}.${key.slice(6, 8)}`;
  }
  return key;
}

function highlight(text, keyword) {
  if (!keyword) return text;
  const idx = text.toLowerCase().indexOf(keyword.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark
        style={{
          background: "transparent",
          color: COLORS.amber,
          fontWeight: 700,
          padding: 0,
        }}
      >
        {text.slice(idx, idx + keyword.length)}
      </mark>
      {text.slice(idx + keyword.length)}
    </>
  );
}

const MIN_KEYWORD_LENGTH = 2;
const DEBOUNCE_MS = 250;

// import된 JSON을 한 번만 평탄화 + ARTISTCOMMENT 파싱해서 모듈 로드 시 딱 한 번만 계산한다.
// (검색할 때마다 JSON.parse를 반복하지 않기 위함 — 대용량 데이터에서의 부하 원인)
function buildFlatEntries(rawData) {
  const flat = [];
  Object.entries(rawData).forEach(([dateKey, list]) => {
    if (!Array.isArray(list)) return;
    list.forEach((item) => {
      const { artistName, comment, imgUrl } = parseArtistComment(
        item.ARTISTCOMMENT
      );
      if (!comment) return;
      flat.push({
        date: dateKey,
        artistName,
        comment,
        commentLower: comment.toLowerCase(),
        imgUrl,
        commentSeq: item.COMMENTSEQ,
        dpRegDate: item.DPREGDATE,
      });
    });
  });
  flat.sort((a, b) => (a.dpRegDate < b.dpRegDate ? 1 : -1));
  return flat;
}

const ALL_ENTRIES = buildFlatEntries(allArtistComments);

export default function CommentSearch({ isOpen, onClose, onSelectComment }) {
  const [keyword, setKeyword] = useState("");
  const [debouncedKeyword, setDebouncedKeyword] = useState("");

  // 입력 후 잠깐 멈췄을 때만 검색을 실행해서 매 타이핑마다 대용량 배열을 훑지 않게 한다.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedKeyword(keyword), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [keyword]);

  // 모달이 새로 열릴 때마다 이전 검색어를 비워서 깨끗하게 시작한다.
  useEffect(() => {
    if (isOpen) {
      setKeyword("");
      setDebouncedKeyword("");
    }
  }, [isOpen]);

  const trimmedKeyword = debouncedKeyword.trim();
  const isSearchable = trimmedKeyword.length >= MIN_KEYWORD_LENGTH;

  const results = useMemo(() => {
    if (!isSearchable) return [];
    const kw = trimmedKeyword.toLowerCase();
    return ALL_ENTRIES.filter((e) => e.commentLower.includes(kw));
  }, [isSearchable, trimmedKeyword]);

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        @keyframes commentSearchFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes commentSearchSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .csr-result-row:hover {
          border-color: rgba(242, 199, 68, 0.5);
        }
        .csr-result-row:focus-visible {
          box-shadow: 0 0 0 2px rgba(242, 199, 68, 0.6);
        }
      `}</style>
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          background: COLORS.bg,
          overflowY: "auto",
          animation: "commentSearchFadeIn 0.15s ease-out",
        }}
      >
        <div
          style={{
            minHeight: "100%",
            color: COLORS.text,
            fontFamily:
              "'Pretendard', -apple-system, BlinkMacSystemFont, 'Apple SD Gothic Neo', sans-serif",
            padding: "24px 20px 40px",
            boxSizing: "border-box",
            animation: "commentSearchSlideUp 0.2s ease-out",
          }}
        >
          <div style={{ maxWidth: 640, margin: "0 auto" }}>
            {/* 헤더 */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "right",
                marginBottom: 6,
              }}
            >
              <button
                onClick={onClose}
                aria-label="닫기"
                style={{
                  background: "transparent",
                  border: "none",
                  color: COLORS.muted,
                  cursor: "pointer",
                  fontSize: 20,
                  lineHeight: 1,
                  padding: 6,
                }}
              >
                ✕
              </button>
            </div>

            {/* 검색창 */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                background: COLORS.surface,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 12,
                padding: "12px 16px",
              }}
            >
              <span
                style={{ fontSize: 15, color: COLORS.muted }}
                aria-hidden="true"
              >
                🔍
              </span>
              <input
                autoFocus
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="검색할 키워드를 입력하세요"
                style={{
                  flex: 1,
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  color: COLORS.text,
                  fontSize: 16,
                }}
              />
              {keyword && (
                <button
                  onClick={() => setKeyword("")}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: COLORS.muted,
                    cursor: "pointer",
                    display: "flex",
                    fontSize: 14,
                    lineHeight: 1,
                    padding: 0,
                  }}
                  aria-label="검색어 지우기"
                >
                  ✕
                </button>
              )}
            </div>

            {/* 결과 카운트 / 안내 */}
            {keyword.trim() && !isSearchable ? (
              <div
                style={{
                  fontSize: 12.5,
                  color: COLORS.muted,
                  margin: "14px 2px 10px",
                }}
              >
                {MIN_KEYWORD_LENGTH}글자 이상 입력해주세요
              </div>
            ) : isSearchable ? (
              <div
                style={{
                  fontSize: 12.5,
                  color: COLORS.muted,
                  margin: "14px 2px 10px",
                }}
              >
                <span style={{ color: COLORS.amber, fontWeight: 700 }}>
                  {results.length}
                </span>
                건
              </div>
            ) : null}

            {/* 결과 리스트 */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {results.map((r, i) => (
                <div
                  key={`${r.commentSeq}-${i}`}
                  onClick={() => onSelectComment?.(r.date, r.commentSeq)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelectComment?.(r.date, r.commentSeq);
                    }
                  }}
                  className="csr-result-row"
                  style={{
                    display: "flex",
                    gap: 0,
                    background: COLORS.surface,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 14,
                    padding: "14px 16px",
                    cursor: onSelectComment ? "pointer" : "default",
                  }}
                >
                  {/* 실제 댓글과 동일한 프로필 이미지 스타일 (comment.css의 .profile-image) */}
                  <img
                    className="profile-image"
                    src={r.imgUrl}
                    alt={r.artistName}
                    style={{ width: 22, height: 22, flex: "0 0 22px" }}
                  />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: "space-between",
                        gap: 8,
                      }}
                    >
                      {/* 실제 댓글과 동일한 이름 스타일 (comment.css의 .comment-name, 인증 배지 포함) */}
                      <span className="comment-name">{r.artistName}</span>
                      <span
                        style={{
                          fontSize: 11.5,
                          color: COLORS.amber,
                          fontVariantNumeric: "tabular-nums",
                          flexShrink: 0,
                        }}
                      >
                        {formatDateKey(r.date)}
                      </span>
                    </div>
                    {/* 실제 댓글과 동일한 본문 스타일 (comment.css의 .comment-content) */}
                    <div className="comment-content" style={{ top: 0 }}>
                      {highlight(r.comment, trimmedKeyword)}
                    </div>
                  </div>
                </div>
              ))}

              {isSearchable && results.length === 0 && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "36px 20px",
                    color: COLORS.muted,
                    fontSize: 13.5,
                    border: `1px dashed ${COLORS.border}`,
                    borderRadius: 14,
                  }}
                >
                  "{trimmedKeyword}"가 포함된 댓글을 찾지 못했어요.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
